import { useEffect, useState, useMemo } from "react";
import { api } from "../lib/api";

/**
 * Charge les jours non travaillés couvrant une plage de dates, filtrés
 * facultativement par département.
 *
 * Retourne :
 *  - `items`     : liste brute
 *  - `byDate`    : Map { "YYYY-MM-DD": Entry[] }  (utile pour afficher un
 *                   bandeau par jour)
 *  - `isNonWorking(date, brandId?)` : bool — utile pour skipper une case
 *  - `reload()`  : force un refetch
 */
export function useNonWorkingDays({ startDate, endDate, brandId } = {}) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [reloadToken, setReloadToken] = useState(0);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!startDate || !endDate) return;
      setLoading(true);
      try {
        const params = { start_date: startDate, end_date: endDate };
        if (brandId) params.brand_id = brandId;
        const { data } = await api.get("/non-working-days", { params });
        if (!cancelled) setItems(Array.isArray(data) ? data : []);
      } catch {
        if (!cancelled) setItems([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [startDate, endDate, brandId, reloadToken]);

  const byDate = useMemo(() => {
    const m = {};
    for (const e of items) {
      if (!m[e.date]) m[e.date] = [];
      m[e.date].push(e);
    }
    return m;
  }, [items]);

  const isNonWorking = (date, checkBrandId) => {
    const list = byDate[date] || [];
    if (!list.length) return false;
    if (!checkBrandId) return true;
    return list.some((e) => e.brand_id === checkBrandId);
  };

  return {
    items,
    byDate,
    loading,
    isNonWorking,
    reload: () => setReloadToken((t) => t + 1),
  };
}
